import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  Alert
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsActive as NotificationsActiveIcon
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const ExchangeMessageSlider = ({
  exchanges = ['MCX', 'NFO', 'CDS', 'NSE', 'ALL'],
  height = 150,
  autoplay = true,
  showPagination = true,
  slidesPerView = 1,
  spaceBetween = 20,
  brokerageMessage = null
}) => {
  const theme = useTheme();

  // Get socket messages from Redux store
  const socketMessages = useSelector(state => state.socketMessage || {});

  // Process current messages only for different exchanges and filter by market status
  const processedMessages = useMemo(() => {
    const currentMessages = [];

    // Add brokerage message if it exists (highest priority)
    if (brokerageMessage?.message) {
      currentMessages.push({
        id: 'brokerage-alert',
        messageText: brokerageMessage.message,
        messageType: 'brokerage',
        exchange: 'SYSTEM',
        timestamp: brokerageMessage.lastUpdatedAt || Date.now(),
        marketStatus: 'Open',
        isCurrent: true,
        severity: 'warning'
      });
    }

    // Get current messages from exchange-specific stores only
    if (socketMessages.exchangeMessages) {
      exchanges.forEach(exchange => {
        const exchangeData = socketMessages.exchangeMessages[exchange];
        if (exchangeData && exchangeData.currentMessage) {
          const message = exchangeData.currentMessage;
          // Only include messages where market status is "Open" AND has content
          const hasContent = message.genericMessage || message.bodMessage || message.holidayMessage;

          if (message.marketStatus === "Open" && hasContent) {
            currentMessages.push({
              ...message,
              id: message.id || `current-${exchange}-${message.timestamp}`,
              isCurrent: true
            });
          }
        }
      });
    }

    // Fallback: Get current message from general store if exchange-specific is not available
    if (currentMessages.length === 0 && socketMessages.currentMessage) {
      const msg = socketMessages.currentMessage;
      const hasContent = msg.genericMessage || msg.bodMessage || msg.holidayMessage;

      if (exchanges.includes(msg.exchange) && msg.marketStatus === "Open" && hasContent) {
        currentMessages.push({
          ...msg,
          id: msg.id || `current-${msg.exchange}-${msg.timestamp}`,
          isCurrent: true
        });
      }
    }

    // Sort by timestamp (newest first)
    return currentMessages.sort((a, b) =>
      (b.timestamp || 0) - (a.timestamp || 0)
    );
  }, [socketMessages, exchanges, brokerageMessage]);

  // Get message severity based on content
  const getMessageSeverity = (message) => {
    // Handle brokerage messages
    if (message.messageType === 'brokerage') {
      return message.severity || 'warning';
    }

    // Generic messages should be yellow (warning)
    if (message.genericMessage) {
      return 'warning';
    }

    const text = (message.genericMessage || message.bodMessage || '').toLowerCase();

    if (text.includes('error') || text.includes('failed') || text.includes('issue')) {
      return 'error';
    }
    if (text.includes('warning') || text.includes('caution') || text.includes('special')) {
      return 'warning';
    }
    if (text.includes('success') || text.includes('completed')) {
      return 'success';
    }
    return 'info';
  };

  // Get message icon based on severity and message type
  const getMessageIcon = (severity, message) => {
    // Special icon for brokerage messages
    if (message?.messageType === 'brokerage') {
      return <NotificationsActiveIcon fontSize="small" />;
    }

    switch (severity) {
      case 'error': return <ErrorIcon fontSize="small" />;
      case 'warning': return <WarningIcon fontSize="small" />;
      case 'success': return <CheckCircleIcon fontSize="small" />;
      default: return <InfoIcon fontSize="small" />;
    }
  };


  // If no messages, return null (don't show anything)
  if (processedMessages.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', height: height === 'auto' ? 'auto' : height }}>
      <Swiper
        modules={[Autoplay, Pagination, EffectFade]}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        effect="fade"
        fadeEffect={{
          crossFade: true
        }}
        autoplay={autoplay ? {
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          waitForTransition: false,
          stopOnLastSlide: false
        } : false}
        pagination={showPagination ? {
          clickable: true,
          dynamicBullets: true
        } : false}
        loop={processedMessages.length > 1}
        speed={600}
        allowTouchMove={true}
        grabCursor={true}
        centeredSlides={true}
        style={{
          height: height === 'auto' ? 'auto' : '100%',
          width: '100%',
          '--swiper-pagination-color': theme.palette.primary.main,
          '--swiper-pagination-bullet-inactive-color': theme.palette.action.disabled,
          '--swiper-theme-color': theme.palette.primary.main
        }}
      >
        {processedMessages.map((message) => {
          const severity = getMessageSeverity(message);

          return (
            <SwiperSlide key={message.id}>
              <Box sx={{ height: height === 'auto' ? 'auto' : '100%', width: '100%', p: 0 }}>
                <Alert
                  severity={severity}
                  icon={getMessageIcon(severity, message)}
                  sx={{
                    height: height === 'auto' ? 'auto' : '100%',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                    m: 0,
                    overflow: 'hidden',
                    '& .MuiAlert-message': {
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: height === 'auto' ? 'flex-start' : 'space-between',
                      py: 1,
                      px: 1,
                      overflow: 'hidden'
                    },
                    '& .MuiAlert-icon': {
                      alignSelf: 'flex-start',
                      mt: 0.5,
                      flexShrink: 0
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', px: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {message.messageType === 'brokerage' ? (
                        <>
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: 'warning.dark', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                            Brokerage Update Alert
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.4, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                            {message.messageText}
                          </Typography>
                        </>
                      ) : (
                        <>
                          {message.genericMessage && (
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, wordBreak: 'break-word', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                              {message.genericMessage}
                            </Typography>
                          )}

                          {message.bodMessage && (
                            <Typography variant="body2" sx={{ mb: 0.5, wordBreak: 'break-word', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                              {message.bodMessage}
                            </Typography>
                          )}

                          {message.holidayMessage && (
                            <Typography variant="body2" sx={{ mb: 0.5, color: 'warning.main', wordBreak: 'break-word', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                              🎉 {message.holidayMessage}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Alert>
              </Box>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Box>
  );
};

export default ExchangeMessageSlider;